import { useState, useEffect } from 'react';
import {
  User,
  Lock,
  Bell,
  Globe,
  Palette,
  Key,
  Smartphone,
  Save,
  Eye,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Loader2
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAdminAuth, changeAdminPassword } from '@/services/adminAuth';
import {
  onAdminSettingsChange,
  saveAdminSettings,
  FirestoreAdminSettings
} from '@/services/firebase/adminFirestore';

type SettingsTab = 'profile' | 'security' | 'notifications' | 'appearance' | 'api';

export function AdminSettings() {
  const { session } = useAdminAuth();
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  // Settings state
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(false);
  const [incidentAlerts, setIncidentAlerts] = useState(true);
  const [weeklyReports, setWeeklyReports] = useState(true);
  const [theme, setTheme] = useState<'dark' | 'light' | 'system'>('dark');
  const [accentColor, setAccentColor] = useState('#0ea5e9');

  // Load settings from Firebase
  useEffect(() => {
    const userId = session?.user.id || session?.user.email;
    if (!userId) {
      setIsLoading(false);
      return;
    }

    const unsubscribe = onAdminSettingsChange(userId, (settings: FirestoreAdminSettings | null) => {
      if (settings) {
        // Load profile settings
        setDisplayName(settings.profile.displayName || session?.user.name || '');
        setEmail(settings.profile.email || session?.user.email || '');

        // Load notification settings
        setEmailNotifications(settings.notifications.emailNotifications);
        setPushNotifications(settings.notifications.pushNotifications);
        setIncidentAlerts(settings.notifications.incidentAlerts);
        setWeeklyReports(settings.notifications.weeklyReports);

        // Load appearance settings
        setTheme(settings.appearance.theme);
        setAccentColor(settings.appearance.accentColor);
      } else {
        // Use defaults from session if no settings exist
        setDisplayName(session?.user.name || '');
        setEmail(session?.user.email || '');
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [session?.user.id, session?.user.email, session?.user.name]);

  const tabs = [
    { id: 'profile', label: 'Profile', icon: <User size={18} /> },
    { id: 'security', label: 'Security', icon: <Lock size={18} /> },
    { id: 'notifications', label: 'Notifications', icon: <Bell size={18} /> },
    { id: 'appearance', label: 'Appearance', icon: <Palette size={18} /> },
    { id: 'api', label: 'API Keys', icon: <Key size={18} /> },
  ];

  const handleSave = async () => {
    const userId = session?.user.id || session?.user.email;
    if (!userId) {
      setSaveMessage({ type: 'error', text: 'User not authenticated' });
      return;
    }

    setIsSaving(true);
    setSaveMessage(null);

    try {
      await saveAdminSettings(userId, {
        profile: {
          displayName,
          email,
        },
        notifications: {
          emailNotifications,
          pushNotifications,
          incidentAlerts,
          weeklyReports,
        },
        appearance: {
          theme,
          accentColor,
        },
      });

      setSaveMessage({ type: 'success', text: 'Settings saved successfully!' });
    } catch (error) {
      console.error('Failed to save settings:', error);
      setSaveMessage({ type: 'error', text: 'Failed to save settings. Please try again.' });
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  const handlePasswordChange = async () => {
    setPasswordError('');

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    const result = await changeAdminPassword(session?.user.email || '', currentPassword, newPassword);

    if (result.success) {
      setSaveMessage({ type: 'success', text: 'Password changed successfully!' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } else {
      setPasswordError(result.error || 'Failed to change password');
    }
  };

  // Show loading state while settings are being fetched
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <Loader2 size={32} className="animate-spin text-accent" />
          <p className="text-text-secondary">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Settings</h1>
          <p className="text-text-secondary">Manage your account and application settings</p>
        </div>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="btn btn-primary"
        >
          {isSaving ? (
            <RefreshCw size={16} className="animate-spin" />
          ) : (
            <Save size={16} />
          )}
          Save Changes
        </button>
      </div>

      {/* Save Message */}
      {saveMessage && (
        <div className={cn(
          'flex items-center gap-2 p-4 rounded-lg',
          saveMessage.type === 'success' ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'
        )}>
          {saveMessage.type === 'success' ? <CheckCircle2 size={18} /> : <AlertTriangle size={18} />}
          {saveMessage.text}
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Tabs */}
        <div className="lg:w-64 flex-shrink-0">
          <div className="bg-bg-card border border-border-default rounded-xl p-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as SettingsTab)}
                className={cn(
                  'w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors',
                  activeTab === tab.id
                    ? 'bg-accent/10 text-accent'
                    : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated'
                )}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1">
          <div className="bg-bg-card border border-border-default rounded-xl p-6">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-text-primary">Profile Settings</h2>

                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-accent to-purple-600 flex items-center justify-center text-white text-2xl font-bold">
                    {session?.user.email.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <button className="btn btn-secondary btn-sm">Change Avatar</button>
                    <p className="text-xs text-text-tertiary mt-1">JPG, PNG or GIF. Max 2MB.</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Display Name</label>
                    <input
                      type="text"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Email</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Role</label>
                  <input
                    type="text"
                    value={session?.user.role.replace('_', ' ').toUpperCase() || ''}
                    disabled
                    className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default text-text-tertiary cursor-not-allowed"
                  />
                  <p className="text-xs text-text-tertiary mt-1">Role can only be changed by a super admin.</p>
                </div>
              </div>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-text-primary">Security Settings</h2>

                <div className="bg-bg-elevated rounded-lg p-4">
                  <h3 className="font-medium mb-4">Change Password</h3>

                  {passwordError && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 text-red-500 text-sm mb-4">
                      <AlertTriangle size={16} />
                      {passwordError}
                    </div>
                  )}

                  <div className="space-y-4">
                    <div className="relative">
                      <label className="block text-sm font-medium mb-2">Current Password</label>
                      <input
                        type={showPasswords ? 'text' : 'password'}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className="w-full px-4 py-2 rounded-lg bg-bg-base border border-border-default focus:border-accent focus:outline-none"
                      />
                    </div>
                    <div className="relative">
                      <label className="block text-sm font-medium mb-2">New Password</label>
                      <input
                        type={showPasswords ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full px-4 py-2 rounded-lg bg-bg-base border border-border-default focus:border-accent focus:outline-none"
                      />
                    </div>
                    <div className="relative">
                      <label className="block text-sm font-medium mb-2">Confirm New Password</label>
                      <input
                        type={showPasswords ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full px-4 py-2 rounded-lg bg-bg-base border border-border-default focus:border-accent focus:outline-none"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 cursor-pointer text-sm text-text-secondary">
                        <input
                          type="checkbox"
                          checked={showPasswords}
                          onChange={(e) => setShowPasswords(e.target.checked)}
                          className="w-4 h-4 rounded"
                        />
                        Show passwords
                      </label>
                      <button onClick={handlePasswordChange} className="btn btn-primary btn-sm">
                        Update Password
                      </button>
                    </div>
                  </div>
                </div>

                <div className="bg-bg-elevated rounded-lg p-4">
                  <h3 className="font-medium mb-4">Two-Factor Authentication</h3>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-text-secondary text-sm">Add an extra layer of security to your account</p>
                      <p className="text-xs text-text-tertiary mt-1">Status: Not enabled</p>
                    </div>
                    <button className="btn btn-secondary btn-sm">
                      <Smartphone size={16} />
                      Enable 2FA
                    </button>
                  </div>
                </div>

                <div className="bg-bg-elevated rounded-lg p-4">
                  <h3 className="font-medium mb-4">Active Sessions</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between py-2 border-b border-border-default">
                      <div className="flex items-center gap-3">
                        <Globe size={18} className="text-text-tertiary" />
                        <div>
                          <p className="text-sm text-text-primary">Current Session</p>
                          <p className="text-xs text-text-tertiary">Chrome on macOS - Active now</p>
                        </div>
                      </div>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/10 text-green-500">Current</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-text-primary">Notification Settings</h2>

                <div className="space-y-4">
                  <div className="flex items-center justify-between py-3 border-b border-border-default">
                    <div>
                      <p className="font-medium text-text-primary">Email Notifications</p>
                      <p className="text-sm text-text-secondary">Receive notifications via email</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={emailNotifications}
                        onChange={(e) => setEmailNotifications(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-bg-elevated peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between py-3 border-b border-border-default">
                    <div>
                      <p className="font-medium text-text-primary">Push Notifications</p>
                      <p className="text-sm text-text-secondary">Receive push notifications in browser</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pushNotifications}
                        onChange={(e) => setPushNotifications(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-bg-elevated peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between py-3 border-b border-border-default">
                    <div>
                      <p className="font-medium text-text-primary">Incident Alerts</p>
                      <p className="text-sm text-text-secondary">Get notified when incidents are created</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={incidentAlerts}
                        onChange={(e) => setIncidentAlerts(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-bg-elevated peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between py-3">
                    <div>
                      <p className="font-medium text-text-primary">Weekly Reports</p>
                      <p className="text-sm text-text-secondary">Receive weekly analytics summary</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={weeklyReports}
                        onChange={(e) => setWeeklyReports(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-bg-elevated peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent"></div>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Appearance Tab */}
            {activeTab === 'appearance' && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-text-primary">Appearance Settings</h2>

                <div>
                  <label className="block text-sm font-medium mb-3">Theme</label>
                  <div className="grid grid-cols-3 gap-3">
                    {(['dark', 'light', 'system'] as const).map((themeOption) => (
                      <button
                        key={themeOption}
                        onClick={() => setTheme(themeOption)}
                        className={cn(
                          'p-4 rounded-lg border-2 transition-colors text-center capitalize',
                          theme === themeOption
                            ? 'border-accent bg-accent/10'
                            : 'border-border-default hover:border-accent/50'
                        )}
                      >
                        {themeOption}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-3">Accent Color</label>
                  <div className="flex gap-3">
                    {['#0ea5e9', '#8b5cf6', '#22c55e', '#f59e0b', '#ef4444', '#ec4899'].map((color) => (
                      <button
                        key={color}
                        onClick={() => setAccentColor(color)}
                        className={cn(
                          'w-10 h-10 rounded-full border-2 transition-transform hover:scale-110',
                          accentColor === color ? 'border-white ring-2 ring-accent' : 'border-transparent'
                        )}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* API Keys Tab */}
            {activeTab === 'api' && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-text-primary">API Keys</h2>

                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="text-yellow-500 flex-shrink-0 mt-0.5" size={18} />
                    <div>
                      <p className="text-sm text-yellow-500 font-medium">Keep your API keys secure</p>
                      <p className="text-xs text-yellow-500/70">Never share your API keys publicly or commit them to version control.</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-bg-elevated rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-medium text-text-primary">Production API Key</p>
                        <p className="text-xs text-text-tertiary">Created on Dec 1, 2025</p>
                      </div>
                      <button className="btn btn-secondary btn-sm">
                        <RefreshCw size={14} />
                        Regenerate
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 px-3 py-2 rounded bg-bg-base text-sm font-mono text-text-tertiary">
                        sk_live_••••••••••••••••••••••••
                      </code>
                      <button className="btn btn-secondary btn-sm">
                        <Eye size={14} />
                      </button>
                    </div>
                  </div>

                  <div className="bg-bg-elevated rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-medium text-text-primary">Development API Key</p>
                        <p className="text-xs text-text-tertiary">Created on Dec 15, 2025</p>
                      </div>
                      <button className="btn btn-secondary btn-sm">
                        <RefreshCw size={14} />
                        Regenerate
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 px-3 py-2 rounded bg-bg-base text-sm font-mono text-text-tertiary">
                        sk_test_••••••••••••••••••••••••
                      </code>
                      <button className="btn btn-secondary btn-sm">
                        <Eye size={14} />
                      </button>
                    </div>
                  </div>
                </div>

                <button className="btn btn-secondary">
                  <Key size={16} />
                  Create New API Key
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
